import { createContext, useContext } from 'react';

export enum Environment {
  DEV = 'dev',
  STAGING = 'staging',
  PRODUCTION = 'production',
  UNKNOWN = 'unknown',
}

export type EnvironmentData = {
  enableTracking: boolean;
  keycloakConfig: {
    url: string;
    realm: string;
    clientId: string;
  };
  launchDarklyConfig: {
    clientSideID: string | null;
  };
  newRelicConfig: {
    accountId: string | null;
    licenseKey: string | null;
    appId: string | null;
  };
  classicUrl: string;
  environment: Environment;
  graURL: string;
  reputationUrl: string;
  theInsightsUrl: string;
};

export type EnvironmentContract = EnvironmentData & {
  version: string;
  date: string;
  apiEndpoint: string;
  baseUrl: string;
};

const FIRST_SUBDOMAIN_REGEX = /^([\w-_]+)/;

export const calculateApiEndpointDefault = (hostname: string, port: number) => {
  const apiHostname = hostname.includes('.')
    ? hostname.replace(FIRST_SUBDOMAIN_REGEX, 'api')
    : `api.${hostname}`;
  return port !== 80
    ? `${apiHostname}:${port}/datapoints`
    : `${apiHostname}/datapoints`;
};

export const defaultContextValue = {
  apiEndpoint: calculateApiEndpointDefault(
    window.location.hostname,
    parseInt(window.location.port, 10) || 80,
  ),
  version: '',
  date: '',
  baseUrl: '',
  enableTracking: false,
  keycloakConfig: {
    url: '',
    realm: '',
    clientId: '',
  },
  launchDarklyConfig: {
    clientSideID: null,
  },
  newRelicConfig: {
    accountId: null,
    licenseKey: null,
    appId: null,
  },
  classicUrl: '',
  environment: Environment.UNKNOWN,
  graURL: '',
  domainName: '',
  reputationUrl: '',
  theInsightsUrl: '',
};

const EnvironmentContext = createContext<EnvironmentContract>({
  ...defaultContextValue,
});

export const useEnvironment = () => useContext(EnvironmentContext);

export default EnvironmentContext;
